import type * as THREE from 'three';
import type * as MatSDKTypes from '../WasmModules/MatSDK';
import type { IWrapper } from '@/engine/DataFlow/DataTypes';
import { BaseValue, WrappingValue } from '@/engine/DataFlow/DataTypes';
import type { IEmLifetime } from '../WasmModules/MatSDK/Types/Interfaces';
import type {
  GenericDevice,
  IInstrumentationModelsProvider,
  Instrumentation,
  Manufacturer,
  System
} from '../Instrumentation/Interfaces';
import { DownloadGeometryAsDraco } from '@/engine/Utilities/DracoUtils';

export * from '@/engine/DataFlow/DataTypes';

//////////////////////////////////////////////////////////////

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function MatWrappingValue<T extends IEmLifetime>(typeName: string) {
  abstract class _MatWrappingValue extends WrappingValue<T>(typeName) {
    public dispose(): void {
      this.value.delete();
    }
  }
  return _MatWrappingValue;
}

//////////////////////////////////////////////////////////////

export class MatTriangleSurface extends MatWrappingValue<MatSDKTypes.TriangleSurface>('DT.MatTriangleSurface') {}
export class MatGrid extends MatWrappingValue<MatSDKTypes.Grid>('DT.MatGrid') {}
export class MatKDTree3d extends MatWrappingValue<MatSDKTypes.KDTree3d>('DT.MatKDTree3d') {}
export class MatVectorKDTree3d extends MatWrappingValue<MatSDKTypes.VectorKDTree3d>('DT.MatVectorKDTree3d') {}
export class MatLineSegments extends MatWrappingValue<MatSDKTypes.LineSegments>('DT.MatLineSegments') {}
export class MatPolylinesVector extends MatWrappingValue<MatSDKTypes.VectorPolyline3d>('DT.MatPolylinesVector') {}
export class MatPolyline extends MatWrappingValue<MatSDKTypes.Polyline3d>('DT.MatPolyline') {}
export class MatDoublesVector extends MatWrappingValue<MatSDKTypes.VectorDouble>('DT.MatDoublesVector') {}
export class MatSegmentsGenerator extends MatWrappingValue<MatSDKTypes.SegmentsGenerator>('DT.MatSegmentsGenerator') {}
export class MatCenterlineTree extends MatWrappingValue<MatSDKTypes.CTree>('DT.MatCenterlineTree') {}
export class MatAASegmentsMapper extends MatWrappingValue<MatSDKTypes.AASegmentsMapper>('DT.MatAASegmentsMapper') {}
export class MatCenterlineSelector extends MatWrappingValue<MatSDKTypes.CenterlineSelector>(
  'DT.MatCenterlineSelector'
) {}
export class MatVectorTriangleSurface extends MatWrappingValue<MatSDKTypes.VectorTriangleSurface>(
  'DT.MatVectorTriangleSurface'
) {}

//////////////////////////////////////////////////////////////

export class Mesh extends WrappingValue<THREE.BufferGeometry>('DT.Mesh') {
  public download(): void {
    DownloadGeometryAsDraco(this.value, 'ModelExport.drc');
  }
}
export class LineSegmentsMesh extends WrappingValue<THREE.BufferGeometry>('DT.LineSegmentsMesh') {}
export class PolylineMesh extends WrappingValue<THREE.BufferGeometry>('DT.PolylineMesh') {}
export class TransformMatrix extends WrappingValue<THREE.Matrix4>('DT.TransformMatrix') {}
export class Point extends WrappingValue<THREE.Vector3>('DT.Point') {}
export class Vector extends WrappingValue<THREE.Vector3>('DT.Vector') {}
export class Line extends WrappingValue<THREE.Line3>('DT.Line') {}

export class PointsArray extends WrappingValue<Array<THREE.Vector3>>('DT.PointsArray') {}

export interface PolylineT {
  points: Array<THREE.Vector3>;
  isClosed: boolean;
}
export class Polyline extends WrappingValue<PolylineT>('DT.Polyline') {}

export interface PolylinesVectorT {
  polylines: Array<Polyline>;
}
export class PolylinesVector extends WrappingValue<PolylinesVectorT>('DT.PolylinesVector') {}

export interface CenterlineT {
  points: Array<THREE.Vector3>;
  normals: Array<THREE.Vector3>;
}
export class Centerline extends WrappingValue<CenterlineT>('DT.Centerline') {}

export interface PlaneT {
  planeEq: THREE.Plane;
  origin: THREE.Vector3;
}
export class Plane extends WrappingValue<PlaneT>('DT.Plane') {}

export interface SplineT {
  basePts: Array<THREE.Vector3>;
  splinePts: Array<THREE.Vector3>;
  isClosed: boolean;
  center: THREE.Vector3;
  normal: THREE.Vector3;
}
export class Spline extends WrappingValue<SplineT>('DT.Spline') {}

//////////////////////////////////////////////////////////////

export interface IMetadataHolder<TMeta extends Record<string, unknown>> {
  readonly metadata: TMeta;
}

export interface IDevice {
  readonly name: string;
  getModelAsync: () => Promise<THREE.BufferGeometry>;
}

export interface DeviceConstructor<TDevice extends IDevice> {
  new (device: GenericDevice, modelProvider: IInstrumentationModelsProvider): TDevice;
  readonly DeviceType: string;
}
export interface ISystem {
  getAvailableDevicesNames: (deviceType: string) => Array<string>;
  getAllDevicesAs: <TDevice extends IDevice>(constructor: DeviceConstructor<TDevice>) => Array<TDevice>;
  getDeviceAs: <TDevice extends IDevice>(constructor: DeviceConstructor<TDevice>, name: string) => TDevice;
}

export type SystemConstructor<TSystem extends ISystem> = new (
  system: System,
  modelProvider: IInstrumentationModelsProvider
) => TSystem;

export interface IManufacturer {
  getPreferredSystemName: () => string;
  getAvailableSystemsNames: () => Array<string>;
  getSystemAs: <TSystem extends ISystem>(constructor: SystemConstructor<TSystem>, name: string) => TSystem;
}

export type ManufacturerConstructor<TManufacturer extends IManufacturer> = new (
  manufacturer: Manufacturer,
  modelProvider: IInstrumentationModelsProvider
) => TManufacturer;

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/explicit-function-return-type
export function ManufacturerT(typeName: string) {
  abstract class _ManufacturerT extends BaseValue(typeName) implements IManufacturer {
    private readonly _manufacturer: Manufacturer;

    private readonly _modelsProvider: IInstrumentationModelsProvider;

    public constructor(manufacturer: Manufacturer, modelsProvider: IInstrumentationModelsProvider) {
      super();
      this._manufacturer = manufacturer;
      this._modelsProvider = modelsProvider;
    }

    public getPreferredSystemName(): string {
      return this._manufacturer.preferredSystem;
    }

    public getAvailableSystemsNames(): Array<string> {
      return this._manufacturer.systems.map((s) => s.name);
    }

    public getSystemAs<TSystem extends ISystem>(constructor: SystemConstructor<TSystem>, name: string): TSystem {
      const found = this._manufacturer.systems.find((s) => s.name == name);
      if (!found) {
        throw new Error('System is not found');
      }
      return new constructor(found, this._modelsProvider);
    }
  }
  return _ManufacturerT;
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/explicit-function-return-type
export function SystemWithMetadata<TSystemMeta extends Record<string, unknown>>(typeName: string) {
  abstract class _SystemWithMetadata extends BaseValue(typeName) implements ISystem, IMetadataHolder<TSystemMeta> {
    public readonly metadata: TSystemMeta;

    private readonly _system: System;

    private readonly _modelsProvider: IInstrumentationModelsProvider;

    public constructor(system: System, modelsProvider: IInstrumentationModelsProvider) {
      super();
      this._system = system;
      this._modelsProvider = modelsProvider;
      this.metadata = system.metadata as TSystemMeta;
    }

    public getAvailableDevicesNames(deviceType: string): Array<string> {
      return this._system.devices.filter((d) => d.type == deviceType).map((d) => d.name);
    }

    public getAllDevicesAs<TDevice extends IDevice>(constructor: DeviceConstructor<TDevice>): Array<TDevice> {
      return this._system.devices
        .filter((d) => d.type == constructor.DeviceType)
        .map((d) => new constructor(d, this._modelsProvider));
    }

    public getDeviceAs<TDevice extends IDevice>(constructor: DeviceConstructor<TDevice>, name: string): TDevice {
      const found = this._system.devices.find((d) => d.type == constructor.DeviceType && d.name == name);
      if (!found) {
        throw new Error('Device is not found');
      }
      return new constructor(found, this._modelsProvider);
    }
  }
  return _SystemWithMetadata;
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/explicit-function-return-type
export function DeviceWithMetadata<TMeta extends Record<string, unknown>>(typeName: string, deviceType: string) {
  abstract class _DeviceWithMetadata extends BaseValue(typeName) implements IDevice, IMetadataHolder<TMeta> {
    public static readonly DeviceType: string = deviceType;

    public readonly metadata: TMeta;

    public readonly name: string;

    private readonly _device: GenericDevice;

    private readonly _modelsProvider: IInstrumentationModelsProvider;

    public constructor(device: GenericDevice, modelsProvider: IInstrumentationModelsProvider) {
      super();
      this.name = device.name;
      this._device = device;
      this._modelsProvider = modelsProvider;
      this.metadata = device.metadata as TMeta;
    }

    public getModelAsync(): Promise<THREE.BufferGeometry> {
      return this._modelsProvider.provideModelAsync(this._device.modelUrl);
    }
  }
  return _DeviceWithMetadata;
}

export class InstrumentationLibrary extends BaseValue('DT.InstrumentationLibrary') {
  public readonly instrumentation: Instrumentation;

  private readonly _modelsProvider: IInstrumentationModelsProvider;

  public constructor(instr: Instrumentation, provider: IInstrumentationModelsProvider) {
    super();
    this.instrumentation = instr;
    this._modelsProvider = provider;
  }

  public getAvailableManufacturers(): Array<string> {
    return this.instrumentation.manufacturers.map((m) => m.name);
  }

  public getManufacturerAs<TManufacturer extends IManufacturer>(
    constructor: ManufacturerConstructor<TManufacturer>,
    manufacturerName: string
  ): TManufacturer {
    const found = this.instrumentation.manufacturers.find((m) => m.name == manufacturerName);
    if (!found) {
      throw new Error('Manufacturer is not found');
    }
    return new constructor(found, this._modelsProvider);
  }
}

//////////////////////////////////////////////////////////////

export interface OrientationVectors {
  top: THREE.Vector3;
  dir: THREE.Vector3;
}

export class Orientation extends WrappingValue<OrientationVectors>('DT.Orientation') {}

//////////////////////////////////////////////////////////////

export interface RegionOfInterestT {
  ptO: THREE.Vector3;
  dirZ: THREE.Vector3;
  dirY: THREE.Vector3;
  minCorner: THREE.Vector3;
  maxCorner: THREE.Vector3;
}

export class RegionOfInterest extends WrappingValue<RegionOfInterestT>('DT.RegionOfInterest') {}

//////////////////////////////////////////////////////////////

export interface CriteriaRangeT {
  greaterThan: number | undefined;
  lessThan: number | undefined;
}

export class CriteriaRange extends WrappingValue<CriteriaRangeT>('DT.CriteriaRange') {}

export enum CheckStatusT {
  Yes = 'yes',
  Maybe = 'maybe',
  No = 'no'
}

export class CheckStatus extends WrappingValue<CheckStatusT>('DT.CheckStatus') {}

export interface CheckCriteriaT {
  value: number;
  defaultStatus: CheckStatusT;
  status: CheckStatusT;
  yesRange?: CriteriaRangeT;
  maybeRange?: CriteriaRangeT;
  noRange?: CriteriaRangeT;
}

export class CheckCriteria extends WrappingValue<CheckCriteriaT>('DT.CheckCriteria') {}

//////////////////////////////////////////////////////////////

export interface IAreaMeasurementParams {
  basePts: Array<THREE.Vector3>;
  splinePts: Array<THREE.Vector3>;
  area: number;
  perimeter: number;
  dA: number;
  dP: number;
  cs: THREE.Matrix4;
  lMin?: THREE.Line3;
  lMax?: THREE.Line3;
  lPerp?: THREE.Line3;
  dMean?: number;
  lX?: THREE.Line3;
  lY?: THREE.Line3;
}

export interface ILocatable {
  getLocation: () => RegionOfInterestT | undefined;
}

export interface IWithCriteria {
  getCriteria: () => CheckCriteriaT | undefined;
}

/* eslint-disable @typescript-eslint/parameter-properties */
export class AreaMeasurement extends BaseValue('DT.AreaMeasurement') implements ILocatable {
  public readonly dMin?: number;

  public readonly dMax?: number;

  public readonly dPerp?: number;

  public readonly dX?: number;

  public readonly dY?: number;

  public constructor(
    public readonly basePts: Array<THREE.Vector3>,
    public readonly splinePts: Array<THREE.Vector3>,
    public readonly area: number,
    public readonly perimeter: number,
    public readonly dA: number,
    public readonly dP: number,
    public readonly center: THREE.Vector3,
    public readonly normal: THREE.Vector3,
    public readonly orientationY?: THREE.Vector3,
    public readonly minCorner?: THREE.Vector3,
    public readonly maxCorner?: THREE.Vector3,
    public readonly lMin?: THREE.Line3,
    public readonly lMax?: THREE.Line3,
    public readonly lPerp?: THREE.Line3,
    public readonly dMean?: number,
    public readonly lX?: THREE.Line3,
    public readonly lY?: THREE.Line3
  ) {
    super();
    this.dMin = lMin?.distance();
    this.dMax = lMax?.distance();
    this.dPerp = lPerp?.distance();
    this.dX = lX?.distance();
    this.dY = lY?.distance();
  }

  public getLocation(): RegionOfInterestT | undefined {
    if (!this.orientationY || !this.minCorner || !this.maxCorner) return undefined;
    return {
      ptO: this.center,
      dirZ: this.normal,
      dirY: this.orientationY,
      minCorner: this.minCorner,
      maxCorner: this.maxCorner
    };
  }
}
/* eslint-enable @typescript-eslint/parameter-properties */

//////////////////////////////////////////////////////////////
export class SplineMeasurement extends BaseValue('DT.SplineMeasurement') implements ILocatable {
  public readonly length: number;

  public readonly splinePts: Array<THREE.Vector3>;

  public readonly isClosed: boolean;

  public readonly center: THREE.Vector3;

  public readonly normal: THREE.Vector3;

  public constructor(
    splinePts: Array<THREE.Vector3>,
    isClosed: boolean,
    length: number,
    center: THREE.Vector3,
    normal: THREE.Vector3
  ) {
    super();
    this.splinePts = splinePts;
    this.isClosed = isClosed;
    this.length = length;
    this.center = center;
    this.normal = normal;
  }

  public getLocation(): RegionOfInterestT | undefined {
    return undefined;
  }
}

//////////////////////////////////////////////////////////////

export class DistanceMeasurement extends BaseValue('DT.DistanceMeasurement') implements ILocatable, IWithCriteria {
  private readonly _value: number;

  private readonly _line: THREE.Line3;

  private readonly _location?: RegionOfInterestT;

  private readonly _criteria?: CheckCriteriaT;

  public constructor(dist: number, ln: THREE.Line3, location?: RegionOfInterestT, criteria?: CheckCriteriaT) {
    super();
    this._value = dist;
    this._line = ln;
    this._location = location;
    this._criteria = criteria;
  }

  public get distance(): number {
    return this._value;
  }

  public get line(): THREE.Line3 {
    return this._line;
  }

  public getLocation(): RegionOfInterestT | undefined {
    return this._location;
  }

  public getCriteria(): CheckCriteriaT | undefined {
    return this._criteria;
  }
}

//////////////////////////////////////////////////////////////

export class AngleMeasurement extends BaseValue('DT.AngleMeasurement') implements ILocatable, IWithCriteria {
  private readonly _value: number;

  public readonly point1: THREE.Vector3;

  public readonly point2: THREE.Vector3;

  public readonly pointMiddle: THREE.Vector3;

  private readonly _location?: RegionOfInterestT;

  private readonly _criteria?: CheckCriteriaT;

  public constructor(
    angleRad: number,
    ptMid: THREE.Vector3,
    pt1: THREE.Vector3,
    pt2: THREE.Vector3,
    location?: RegionOfInterestT,
    criteria?: CheckCriteriaT
  ) {
    super();
    this.point1 = pt1;
    this.point2 = pt2;
    this.pointMiddle = ptMid;
    this._value = angleRad;
    this._location = location;
    this._criteria = criteria;
  }

  public get angleRad(): number {
    return this._value;
  }

  public get angleDeg(): number {
    return this._value * (180 / Math.PI);
  }

  public getLocation(): RegionOfInterestT | undefined {
    return this._location;
  }

  public getCriteria(): CheckCriteriaT | undefined {
    return this._criteria;
  }
}

//////////////////////////////////////////////////////////////

export class ValueMeasurement extends BaseValue('DT.ValueMeasurement') implements IWrapper<number>, IWithCriteria {
  private readonly _value: number;

  private readonly _criteria?: CheckCriteriaT;

  public constructor(value: number, criteria?: CheckCriteriaT) {
    super();
    this._value = value;
    this._criteria = criteria;
  }

  public get value(): number {
    return this._value;
  }

  public getCriteria(): CheckCriteriaT | undefined {
    return this._criteria;
  }
}

//////////////////////////////////////////////////////////////
